import { useState, useEffect } from 'react';
import api from '../api';

const useProjectsManager = ({ onProjectSelected, onNewProject }) => {
    const [projects, setProjects] = useState([]);
    const [currentProject, setCurrentProject] = useState(null);
    const [displayDeletePopup, setDisplayDeletePopup] = useState(false);
    const [deleteProjectData, setDeleteProjectData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [editingProjectId, setEditingProjectId] = useState(null);
    const [editingProjectName, setEditingProjectName] = useState('');
    const [isUpdatingName, setIsUpdatingName] = useState(false);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await api.get('/projects/user');
            
            // Assurez-vous que response.data est un tableau
            if (Array.isArray(response.data)) {
                setProjects(response.data);
                console.log("Projects loaded:", response.data.length);
            } else {
                console.error("Invalid projects data format:", response.data);
                setProjects([]);
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const showProjectDeletePopup = (project) => {
        setDeleteProjectData(project);
        setDisplayDeletePopup(true);
    };

    const handleDeleteProject = async () => {
        setIsDeleting(true);
        try {
            await api.delete('/delete_project', {
                data: { project_id: deleteProjectData.id }
            });

            const updatedProjects = projects.filter(p => p.id !== deleteProjectData.id);
            setProjects(updatedProjects);
            setDisplayDeletePopup(false);

            if (updatedProjects.length > 0) {
                onProjectSelected(updatedProjects[0].id);
            } else {
                onNewProject();
            }
        } catch (error) {
            console.error('Erreur lors de la suppression du projet :', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const startEditingProject = (project, e) => {
        e.stopPropagation();
        setEditingProjectId(project.id);
        setEditingProjectName(project.name);
    };

    const cancelEditingProject = () => {
        setEditingProjectId(null);
        setEditingProjectName('');
    };

    const updateProjectName = async (projectId) => {
        if (!editingProjectName.trim()) {
            cancelEditingProject();
            return;
        }

        setIsUpdatingName(true);
        try {
            await api.put(`/projects/${projectId}`, {
                name: editingProjectName
            });

            // Mettre à jour la liste locale des projets
            setProjects(projects.map(p => 
                p.id === projectId 
                    ? { ...p, name: editingProjectName } 
                    : p
            ));

            // Si le projet édité est le projet actuel, mettre à jour l'application
            if (projectId === currentProject) {
                // Récupérer les données mises à jour du projet
                const response = await api.get(`/projects/${projectId}`);
                onProjectSelected(projectId); // Refresh the current project
            }
        } catch (error) {
            console.error('Error updating project name:', error);
        } finally {
            setIsUpdatingName(false);
            setEditingProjectId(null);
        }
    };

    const handleEditKeyDown = (e, projectId) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            updateProjectName(projectId);
        } else if (e.key === 'Escape') {
            cancelEditingProject();
        }
    };

    return {
        projects,
        currentProject,
        setCurrentProject,
        loading,
        showProjectDeletePopup,
        displayDeletePopup,
        setDisplayDeletePopup,
        deleteProjectData,
        isDeleting,
        handleDeleteProject,
        refreshProjects: fetchProjects,
        // Fonctions d'édition
        editingProjectId,
        editingProjectName,
        setEditingProjectName,
        startEditingProject,
        cancelEditingProject,
        updateProjectName,
        handleEditKeyDown,
        isUpdatingName
    };
};

export default useProjectsManager; 